import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  deleteDoc,
  Timestamp
} from "firebase/firestore";
import { db, storage } from "./firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Typography,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Chip,
  Collapse,
  LinearProgress,
  Stack,
  Divider
} from "@mui/material";

const CUSTOMER_TYPES = [
  { value: "project_hotel", label: "مشروع - فندق" },
  { value: "project_restaurant", label: "مشروع - مطعم" },
  { value: "project_cafe", label: "مشروع - كافيه" },
  { value: "project_salon", label: "مشروع - صالون" },
  { value: "project_clinic", label: "مشروع - عيادة" },
  { value: "project_beauty_center", label: "مشروع - مركز تجميل" },
  { value: "project_hospital", label: "مشروع - مستشفى" },
  { value: "engineer_designer", label: "مهندس أو مصمم" },
  { value: "engineering_company", label: "شركة هندسية أو تصميم داخلي" },
  { value: "other_company", label: "شركات أخرى" },
  { value: "apartment", label: "شقة" },
  { value: "influencer", label: " انفلونسر / بلوكر / مشهور" },
  { value: "furniture_company", label: "شركة اثاث" },
  { value: "house", label: "بيت" }
];

function safeToDate(ts) {
  try {
    return ts?.toDate ? ts.toDate() : null;
  } catch {
    return null;
  }
}

function formatDateTime(ts) {
  const d = safeToDate(ts);
  if (!d) return "-";
  return d.toLocaleString();
}

// رفع مجموعة ملفات مع Progress
async function uploadFilesWithProgress({ files, folderPath, onProgress }) {
  if (!files || files.length === 0) return [];

  const uploadedUrls = [];
  let totalBytes = 0;
  files.forEach((f) => (totalBytes += f.size));

  let uploadedBytesSoFar = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const cleanName = file.name.replace(/\s+/g, "_");
    const fileRef = ref(storage, `${folderPath}/${Date.now()}-${cleanName}`);

    const task = uploadBytesResumable(fileRef, file);

    const url = await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          // نحسب تقدم هذا الملف فقط
          const fileUploaded = snap.bytesTransferred;
          const previousFilesBytes = uploadedBytesSoFar;

          const overall = Math.min(
            100,
            Math.round(((previousFilesBytes + fileUploaded) / totalBytes) * 100)
          );

          if (onProgress) onProgress(overall);
        },
        (err) => reject(err),
        async () => {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });

    uploadedUrls.push(url);
    uploadedBytesSoFar += file.size; // بعد اكتمال هذا الملف
    if (onProgress) onProgress(Math.min(100, Math.round((uploadedBytesSoFar / totalBytes) * 100)));
  }

  return uploadedUrls;
}

export default function SiteVisitDashboard({ currentUser }) {
  const navigate = useNavigate();

 const [formData, setFormData] = useState({
  customerName: "",
  phone: "",
  customerType: "",
 governorate: "baghdad",
  address: "",
  visitDate: "",
  source: "",
  note: "",

  // 🔥 نفس الزبون المحتمل
  companyName: "",
  facebook: "",
  instagram: "",
  website: "",
  attachmentLink: "",
  priority: "Medium",
});

  const [visits, setVisits] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(null);

  // تقرير
  const [reportOpen, setReportOpen] = useState(null);
  const [reportNote, setReportNote] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // ملفات مختارة قبل الإرسال

  // Upload UI
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Edit
  const [editingId, setEditingId] = useState(null);

  const fetchVisits = async () => {
    const snapshot = await getDocs(collection(db, "siteVisits"));
    const data = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data()
    }));

    // ترتيب من الأحدث إلى الأقدم حسب createdAt
    data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setVisits(data);
  };

  useEffect(() => {
    fetchVisits();
  }, []);

const BAGHDAD_USERS = ["Baneen@123", "maryam@123"];
const ERBIL_USERS = ["jumana@123", "karina@123"];
const ADMIN_USERS = ["admin"]; // أو username مال المدير
const [selectedEmployee, setSelectedEmployee] = useState("all");

const myVisits = useMemo(() => {
  let filtered = visits;

  // 👨‍💼 المدير
  if (ADMIN_USERS.includes(currentUser)) {
    if (selectedEmployee !== "all") {
      filtered = filtered.filter(
        (v) => v.createdBy === selectedEmployee
      );
    }
    return filtered;
  }

  // 👇 الموظفين
  return visits.filter((v) => {
    if (v.assignedTo) {
      return v.assignedTo === currentUser;
    }

    if (v.branch === "baghdad" && BAGHDAD_USERS.includes(currentUser)) {
      return true;
    }

    if (v.branch === "erbil" && ERBIL_USERS.includes(currentUser)) {
      return true;
    }

    return false;
  });
}, [visits, currentUser, selectedEmployee]);


  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

const resetForm = () => {
  setFormData({
    customerName: "",
    phone: "",
    customerType: "",
    governorate: "",
    address: "",
    visitDate: "",
     source: "",
    note: "",

    // 🔥 هاي الحقول انت ضفتها جديد
    companyName: "",
    facebook: "",
    instagram: "",
    website: "",
    attachmentLink: "",
    priority: "Medium",
  });

  setEditingId(null);
};

  const handleSubmit = async () => {
    const { customerName, phone, customerType, governorate, visitDate } = formData;

    if (!customerName?.trim()) return alert("يرجى إدخال اسم الزبون");
    if (!phone?.toString()?.trim()) return alert("يرجى إدخال رقم الهاتف");
    if (!customerType) return alert("يرجى اختيار نوع الزبون");
    if (!governorate) return alert("يرجى اختيار المحافظة");
    if (!visitDate) return alert("يرجى اختيار تاريخ ووقت الزيارة");
    if (!formData.attachmentLink?.trim()) return alert("يرجى إدخال رابط OneDrive");
if (!formData.note?.trim()) return alert("يرجى إدخال ملاحظات الزيارة");
if (!formData.source) return alert("يرجى اختيار مصدر الزبون");

const branch = governorate; // erbil أو baghdad

    if (editingId) {
      await updateDoc(doc(db, "siteVisits", editingId), {
        customerName: formData.customerName,
        phone: parseInt(formData.phone, 10),
        customerType: formData.customerType,
        governorate: formData.governorate,
        address: formData.address || "",
        visitDate: Timestamp.fromDate(new Date(formData.visitDate)),
        note: formData.note || ""
      });
      resetForm();
      fetchVisits();
      return;
    }

   await addDoc(collection(db, "siteVisits"), {
  customerName: formData.customerName.trim(),
  phone: parseInt(formData.phone, 10),
  customerType: formData.customerType,
  governorate: formData.governorate,
  address: formData.address || "",
  visitDate: Timestamp.fromDate(new Date(formData.visitDate)),
  note: formData.note || "",
  createdBy: currentUser,
  branch,
  assignedTo: null,
  status: "scheduled", // 🔥 هذا هو المهم
  createdAt: Timestamp.now(),
  companyName: formData.companyName || "",
facebook: formData.facebook || "",
instagram: formData.instagram || "",
website: formData.website || "",
attachmentLink: formData.attachmentLink || "",
source: formData.source || "",
priority: formData.priority || "Medium",
  report: null
});


    resetForm();
    fetchVisits();
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("هل تريد حذف هذه الزيارة؟");
    if (!ok) return;
    await deleteDoc(doc(db, "siteVisits", id));
    fetchVisits();
  };

  const handleEdit = (visit) => {
    setEditingId(visit.id);
    setFormData({
      customerName: visit.customerName || "",
      phone: (visit.phone ?? "").toString(),
      customerType: visit.customerType || "",
      governorate: visit.governorate || "",
      address: visit.address || "",
      visitDate: safeToDate(visit.visitDate)
        ? safeToDate(visit.visitDate).toISOString().slice(0, 16)
        : "",
      note: visit.note || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openReport = (visitId) => {
    setReportOpen(visitId);
    setReportNote("");
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploading(false);
  };

  const submitReport = async (visitId, dealStatus) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      const folderPath = `siteVisits/${visitId}`;
      const urls = await uploadFilesWithProgress({
        files: selectedFiles,
        folderPath,
        onProgress: setUploadProgress
      });

      const visitRef = doc(db, "siteVisits", visitId);

      await updateDoc(visitRef, {
        status: dealStatus === "won" ? "converted" : "canceled",
        report: {
          note: reportNote || "",
          files: urls,
          fileNames: selectedFiles.map((f) => f.name),
          dealStatus,
          createdAt: Timestamp.now()
        }
      });

      // إذا تمت الصفقة → افتح AddOrder مباشرة
    if (dealStatus === "won") {
  const visit = visits.find((v) => v.id === visitId);

navigate("/leads", {
  state: {
    fromVisit: true,
    leadData: {
      customerName: visit?.customerName || "",
      phone: visit?.phone?.toString() || "",
      customerType: visit?.customerType || "",
      governorate:
        visit?.governorate === "baghdad" ? "بغداد" : "أربيل",
      location: visit?.address || "",
        source: "زيارة موقع",
      description: visit?.note || "",
      source: visit?.source || "",

      // 🔥 هنا التعديل الصحيح
      companyName: visit?.companyName || "",
      facebook: visit?.facebook || "",
      instagram: visit?.instagram || "",
      website: visit?.website || "",
      attachmentLink: visit?.attachmentLink || "",
      priority: visit?.priority || "Medium",

      source: "زيارة موقع",
    },
  },
});
}

      setReportOpen(null);
      setReportNote("");
      setSelectedFiles([]);
      setUploading(false);
      setUploadProgress(0);

      fetchVisits();
    } catch (err) {
      console.error("Upload/Report Error:", err);
      setUploading(false);
      setUploadProgress(0);

      // أشهر خطأ: unauthorized
      alert(
        "فشل رفع الملفات أو حفظ التقرير.\n" +
          "افتح Console (F12) وشوف الخطأ.\n" +
          "غالباً Storage Rules تمنع الرفع."
      );
    }
  };

  const statusChip = (status) => {
    if (status === "scheduled") return { label: "بانتظار الزيارة", color: "primary" };
    if (status === "converted") return { label: "تم تحول الى زبون محتمل", color: "success" };
    return { label: "ملغية", color: "error" };
  };

  return (
    <div style={{ padding: 30, maxWidth: 1100, margin: "auto" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
        📍 إدارة زيارات الموقع
      </Typography>

      {/* ================= FORM ================= */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {editingId ? "✏️ تعديل زيارة" : "➕ إضافة زيارة جديدة"}
          </Typography>

          {editingId && (
            <Button color="inherit" onClick={resetForm}>
              إلغاء التعديل
            </Button>
          )}
        </Stack>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <TextField
              label="اسم الزبون"
              fullWidth
              value={formData.customerName}
              onChange={(e) => handleChange("customerName", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="رقم الهاتف (أرقام فقط)"
              fullWidth
              value={formData.phone}
              onChange={(e) =>
                handleChange("phone", e.target.value.replace(/\D/g, ""))
              }
              inputProps={{ inputMode: "numeric" }}
              helperText="مثال: 07701234567"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>نوع الزبون</InputLabel>
              <Select
                value={formData.customerType}
                onChange={(e) => handleChange("customerType", e.target.value)}
                label="نوع الزبون"
              >
                {CUSTOMER_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          <Grid item xs={12} md={6}>
<FormControl fullWidth>
  <InputLabel>مصدر الزبون *</InputLabel>
  <Select
    value={formData.source}
    label="مصدر الزبون *"
    onChange={(e) => handleChange("source", e.target.value)}
  >
    <MenuItem value="سوشال ميديا">سوشال ميديا</MenuItem>
    <MenuItem value="مجمع البروج - صروح A">مجمع البروج - صروح A</MenuItem>
    <MenuItem value="مجمع البروج - صروح D">مجمع البروج - صروح D</MenuItem>
    <MenuItem value="مجمع النسيم">مجمع النسيم</MenuItem>
    <MenuItem value="الريف كافيه - بغداد">الريف كافيه - بغداد</MenuItem>
    <MenuItem value="الريف كافيه - اربيل">الريف كافيه - اربيل</MenuItem>
    <MenuItem value="ارت تاتش">ارت تاتش</MenuItem>
    <MenuItem value="علاقات ومعارف">علاقات ومعارف</MenuItem>
    <MenuItem value="ميثم برحيه">علاقات ومعارف – ميثم برحيه</MenuItem>
    <MenuItem value="نواف">علاقات ومعارف – نواف</MenuItem>
    <MenuItem value="ست سارة">ست سارة</MenuItem>
  </Select>
</FormControl>
</Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>المحافظة</InputLabel>
              <Select
                value={formData.governorate}
                onChange={(e) => handleChange("governorate", e.target.value)}
                label="المحافظة"
              >
                <MenuItem value="erbil">أربيل</MenuItem>
                <MenuItem value="baghdad">بغداد</MenuItem>
              </Select>
            </FormControl>
          </Grid>
<Grid item xs={12} md={6}>
  <TextField
    label="اسم الشركة"
    fullWidth
    value={formData.companyName}
    onChange={(e) => handleChange("companyName", e.target.value)}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    label="Facebook"
    fullWidth
    value={formData.facebook}
    onChange={(e) => handleChange("facebook", e.target.value)}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    label="Instagram"
    fullWidth
    value={formData.instagram}
    onChange={(e) => handleChange("instagram", e.target.value)}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    label="Website"
    fullWidth
    value={formData.website}
    onChange={(e) => handleChange("website", e.target.value)}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    label="رابط OneDrive"
    fullWidth
    value={formData.attachmentLink}
    onChange={(e) => handleChange("attachmentLink", e.target.value)}
  />
</Grid>

<Grid item xs={12} md={6}>
  <FormControl fullWidth>
    <InputLabel>الأولوية</InputLabel>
    <Select
      value={formData.priority}
      onChange={(e) => handleChange("priority", e.target.value)}
      label="الأولوية"
    >
      <MenuItem value="Low">Low</MenuItem>
      <MenuItem value="Medium">Medium</MenuItem>
      <MenuItem value="High">High</MenuItem>
    </Select>
  </FormControl>
</Grid>
          {formData.governorate === "erbil" && (
            <Grid item xs={12}>
              <TextField
               label="عنوان الزبون بالتفصيل"
                fullWidth
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              type="datetime-local"
              fullWidth
              value={formData.visitDate || ""}
              onChange={(e) => handleChange("visitDate", e.target.value)}
              helperText="حدد تاريخ ووقت الزيارة"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="ملاحظة مختصرة عن الزيارة"
              fullWidth
              value={formData.note}
              onChange={(e) => handleChange("note", e.target.value)}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2, borderRadius: 2, py: 1.2, fontWeight: 800 }}
          onClick={handleSubmit}
        >
          {editingId ? "تحديث الزيارة" : "حفظ الزيارة"}
        </Button>
      </Card>

      {/* ================= LIST ================= */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        📌 زياراتي (الأحدث أولاً)
      </Typography>
{ADMIN_USERS.includes(currentUser) && (
  <FormControl sx={{ mb: 2, minWidth: 200 }}>
    <InputLabel>اختيار الموظفة</InputLabel>
    <Select
      value={selectedEmployee}
      label="اختيار الموظفة"
      onChange={(e) => setSelectedEmployee(e.target.value)}
    >
      <MenuItem value="all">الكل</MenuItem>
      <MenuItem value="Baneen@123">بنين</MenuItem>
     
      <MenuItem value="jumana@123">جمانا</MenuItem>
      <MenuItem value="maysa@123">مياسة</MenuItem>
      <MenuItem value="farah@123">فرح</MenuItem>

      
    </Select>
  </FormControl>
)}
      {myVisits.length === 0 && (
        <Typography sx={{ opacity: 0.7 }}>لا توجد زيارات حالياً.</Typography>
      )}

      {myVisits.map((visit) => {
        const chip = statusChip(visit.status);
        const isDetailsOpen = detailsOpen === visit.id;
        const isReportOpen = reportOpen === visit.id;
        const report = visit.report;

        const customerTypeLabel =
          CUSTOMER_TYPES.find((x) => x.value === visit.customerType)?.label ||
          visit.customerType ||
          "-";

        return (
          <Card key={visit.id} sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <div>
                  <Typography
                    variant="h6"
                    sx={{ cursor: "pointer", fontWeight: 900 }}
                    onClick={() => setDetailsOpen(isDetailsOpen ? null : visit.id)}
                    title="اضغط لعرض كل التفاصيل"
                  >
                    {visit.customerName}
                  </Typography>

                  <Typography sx={{ mt: 0.5 }}>📞 {visit.phone}</Typography>
                  <Typography sx={{ mt: 0.5 }}>
                    🏙️ {visit.governorate === "erbil" ? "أربيل" : "بغداد"}
                  </Typography>
                </div>

                <Chip
                  label={chip.label}
                  color={chip.color}
                  sx={{ fontWeight: 800, mt: 0.5 }}
                />
              </Stack>

              {/* Details */}
              <Collapse in={isDetailsOpen}>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 800 }}>📅 وقت الزيارة</Typography>
                    <Typography>{formatDateTime(visit.visitDate)}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 800 }}>🏷 نوع الزبون</Typography>
                    <Typography>{customerTypeLabel}</Typography>
                  </Grid>

                  {visit.address ? (
                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 800 }}>📍 العنوان</Typography>
                      <Typography>{visit.address}</Typography>
                    </Grid>
                  ) : null}

                  {visit.note ? (
                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 800 }}>📝 ملخص الزيارة</Typography>
                      <Typography>{visit.note}</Typography>
                    </Grid>
                  ) : null}

                  {report ? (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>📋 تقرير الزيارة</Typography>
                      <Typography sx={{ mb: 1 }}>
                        {report.note || "—"}
                      </Typography>

                      {report.files?.length > 0 ? (
                        <div>
                          <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            📎 الملفات/الصور المرفوعة:
                          </Typography>

                          <Stack spacing={1}>
                            {report.files.map((url, idx) => {
                              const name = report.fileNames?.[idx] || `ملف ${idx + 1}`;
                              const isImage = /\.(png|jpg|jpeg|webp|gif)$/i.test(url);

                              return (
                                <div key={idx}>
                                  <a href={url} target="_blank" rel="noreferrer">
                                    {name}
                                  </a>

                                  {isImage && (
                                    <div style={{ marginTop: 8 }}>
                                      <img
                                        src={url}
                                        alt={name}
                                        style={{
                                          maxWidth: 260,
                                          borderRadius: 10,
                                          border: "1px solid #eee"
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </Stack>
                        </div>
                      ) : (
                        <Typography sx={{ opacity: 0.7 }}>لا توجد ملفات مرفوعة.</Typography>
                      )}
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography sx={{ opacity: 0.7 }}>
                        لا يوجد تقرير بعد.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Collapse>

              {/* Actions */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                {visit.status === "scheduled" && (
                  <>
                  {!visit.assignedTo && (
  <Button
    variant="contained"
    color="primary"
    onClick={async () => {
      const visitRef = doc(db, "siteVisits", visit.id);

      // نتأكد أنها لسه غير مستلمة
      if (visit.assignedTo) {
        alert("تم استلام هذه الزيارة من قبل شخص آخر");
        return;
      }

      await updateDoc(visitRef, {
        assignedTo: currentUser
      });

      fetchVisits();
    }}
  >
    📥 استلام الزيارة
  </Button>
)}

                    <Button
  variant="outlined"
  disabled={!visit.assignedTo}
  onClick={() => openReport(visit.id)}
>
                      📋 تقرير
                    </Button>

                  <Button
  color="warning"
  variant="outlined"
  disabled={!visit.assignedTo}
  onClick={() => handleEdit(visit)}
>
                      ✏️ تعديل
                    </Button>

                  
                  </>
                )}

                {/* إذا تحولت إلى طلب، نخلي زر سريع يروح للإضافة مرة ثانية إذا يريد */}
             
              </Stack>

              {/* Report Panel */}
              {isReportOpen && (
                <div style={{ marginTop: 18 }}>
                  <Divider sx={{ mb: 2 }} />

                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    🧾 تقرير الزيارة
                  </Typography>

                  <TextField
                    label="ملاحظات التقرير"
                    multiline
                    rows={4}
                    fullWidth
                    value={reportNote}
                    onChange={(e) => setReportNote(e.target.value)}
                  />

              

              
                  <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={uploading}
                      onClick={() => submitReport(visit.id, "won")}
                      sx={{ fontWeight: 900 }}
                    >
                      ✅ تم تحول الى زبون محتمل
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      disabled={uploading}
                      onClick={() => submitReport(visit.id, "lost")}
                      sx={{ fontWeight: 900 }}
                    >
                      ❌ لم تتم (إلغاء)
                    </Button>

                    <Button
                      variant="text"
                      disabled={uploading}
                      onClick={() => {
                        setReportOpen(null);
                        setReportNote("");
                        setSelectedFiles([]);
                        setUploadProgress(0);
                        setUploading(false);
                      }}
                    >
                      إغلاق
                    </Button>
                  </Stack>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
